import { useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import styled from '@emotion/styled';
import BigTable from '__components/Tables/Big';
import BigTableLayout, {
  useBigTableLayout,
} from '__components/Layout/BigTableLayout';
import PageToolbar, {
  PageSearchInput,
  PageToolbarSection,
  PageToolbarTitle,
} from '__components/Layout/PageToolbar';
import { TRow } from '__components/Kizen/Table';
import { useNavBarState } from 'app/navBarController';
import { APPS_TABS_HEIGHT } from 'pages/Apps/constants';
import { Button } from '@kizen/kds/Button';
import { LinkText } from '@kizen/kds/Typography';
import Notice from '__components/Kizen/Notice';
import Loader from '__components/Kizen/Loader';
import { useBusinessAppPermissions } from 'pages/BusinessApps/permissions';
import { usePaginationSettings } from 'hooks/usePaginationSettings';
import { useBusinessPluginApps } from 'ts-components/Plugins/useBusinessPluginApps';
import { PluginContext } from 'ts-components/Plugins/context';
import { KizenError } from 'services/errors';
import { getOriginalErrorStatusCode } from 'services/AxiosService';
import {
  BUSINESS_APPS_DEVELOPER_DOCS_URL,
  businessAppDetailsUrl,
} from '../../constants';
import {
  type BusinessAppAction,
  type BusinessAppOrdering,
  type BusinessAppTableRow,
  buildTableRows,
  countMissingAppSettings,
  countMissingUserSettings,
  listedApps,
  sortApps,
} from 'ts-components/Plugins/businessApps/helpers';
import {
  type PendingBusinessAppAction,
  useBusinessAppActions,
} from 'ts-components/Plugins/businessApps/useBusinessAppActions';
import { BUSINESS_APP_ACTIONS } from 'ts-components/Plugins/businessApps/constants';
import { ConfirmActionModal } from '../../components/ConfirmActionModal';
import { CreateBusinessAppModal } from '../../components/CreateBusinessAppModal';
import { useBusinessApps } from 'ts-components/Plugins/businessApps/useBusinessApps';
import { kiCodeCopy } from 'ts-components/KiCode/copy';
import { businessAppOpenArgs } from 'ts-components/KiCode/profiles';
import { useKiCode } from 'ts-components/KiCode/useKiCode';
import { useColumns } from './columns';

const StyledNotice = styled(Notice)`
  margin: 20px 20px 0;
`;

const StyledTable = styled.div`
  tbody tr[data-row-kind='version'] td {
    background-color: var(--color-background-lighter);
  }
`;

export const BusinessAppsList = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const permissions = useBusinessAppPermissions();
  const { open: openKiCode } = useKiCode();
  const onActionCompleted = useCallback(
    ({ action, app, version }: PendingBusinessAppAction) => {
      if (action === BUSINESS_APP_ACTIONS.view) {
        history.push(
          businessAppDetailsUrl(app.api_name, version?.preview_owner?.id),
          { from: history.location.pathname }
        );
      } else if (action === BUSINESS_APP_ACTIONS.editConfiguration) {
        history.push(businessAppDetailsUrl(app.api_name, null, '/configure'), {
          from: history.location.pathname,
        });
      }
    },
    [history]
  );
  const { pending, selectAction, confirmPending, cancelPending } =
    useBusinessAppActions({ onCompleted: onActionCompleted });
  const { search, actualSearch, ordering, handleChangeSearch, headData } =
    usePaginationSettings({ ordering: 'name' });
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [showCreateModal, setShowCreateModal] = useState(false);
  const { height } = useNavBarState();
  const { filterDropdownRef, scrollTransitioned, ...bigTablePageProps } =
    useBigTableLayout(height + APPS_TABS_HEIGHT);

  const {
    data,
    isPending,
    isFetching,
    isSuccess,
    isPlaceholderData,
    isError,
    error,
  } = useBusinessApps(search);
  const apps = useMemo(() => listedApps(data ?? []), [data]);

  const rows = useMemo(
    () =>
      buildTableRows(
        sortApps(apps, (ordering || 'name') as BusinessAppOrdering),
        expanded
      ),
    [apps, ordering, expanded]
  );

  const onToggleExpanded = useCallback(
    (apiName: string) =>
      setExpanded((prev) => {
        const next = new Set(prev);
        if (!next.delete(apiName)) {
          next.add(apiName);
        }
        return next;
      }),
    []
  );

  const onOpenDetails = useCallback(
    (apiName: string, previewOwnerId?: string | null) =>
      history.push(businessAppDetailsUrl(apiName, previewOwnerId), {
        from: history.location.pathname,
      }),
    [history]
  );

  const onSelectAction = useCallback(
    ({ value }: { value: BusinessAppAction }, row: BusinessAppTableRow) =>
      selectAction({
        action: value,
        app: row.app,
        version: row.version,
      }),
    [selectAction]
  );

  const { data: businessPluginApps } = useBusinessPluginApps(true);
  const errorCounts = useMemo(
    () =>
      Object.fromEntries(
        (businessPluginApps ?? [])
          .filter((businessPluginApp) => businessPluginApp.plugin_app?.api_name)
          .map((businessPluginApp) => [
            businessPluginApp.plugin_app.api_name,
            businessPluginApp.error_count ?? 0,
          ])
      ),
    [businessPluginApps]
  );

  const { plugins } = useContext(PluginContext);
  const missingUserSettings = useMemo(
    () =>
      Object.fromEntries(
        (businessPluginApps ?? [])
          .filter((businessPluginApp) => businessPluginApp.plugin_app?.api_name)
          .map(({ plugin_app }) => [
            plugin_app.api_name,
            countMissingUserSettings({
              // Bootstrap holds the schema of the version the viewer is running
              base_config:
                plugins?.[plugin_app.api_name]?.base_config ??
                plugin_app.base_config,
              employee_config: plugins?.[plugin_app.api_name]?.employee_config,
            }),
          ])
      ),
    [businessPluginApps, plugins]
  );

  const missingAppSettings = useMemo(
    () =>
      permissions.canPublishBusinessApps
        ? Object.fromEntries(
            (businessPluginApps ?? [])
              .filter(
                (businessPluginApp) => businessPluginApp.plugin_app?.api_name
              )
              .map(({ plugin_app, config }) => [
                plugin_app.api_name,
                countMissingAppSettings({
                  base_config:
                    plugins?.[plugin_app.api_name]?.base_config ??
                    plugin_app.base_config,
                  config,
                }),
              ])
          )
        : {},
    [businessPluginApps, plugins, permissions.canPublishBusinessApps]
  );

  const columns = useColumns({
    permissions,
    errorCounts,
    missingAppSettings,
    missingUserSettings,
    onToggleExpanded,
    onOpenDetails,
    onSelectAction,
  });

  if (isError) {
    throw new KizenError(getOriginalErrorStatusCode(error) ?? 500);
  }

  if (isPending) {
    return <Loader loading />;
  }

  // Debounced search and placeholder data lag behind a cleared search
  const hasNoApps =
    isSuccess && !isPlaceholderData && !search && !actualSearch && !apps.length;

  return (
    <>
      <BigTableLayout
        toolbar={
          <PageToolbar>
            <PageToolbarSection>
              <PageSearchInput
                placeholder={t('Search Apps')}
                value={actualSearch}
                onChange={handleChangeSearch}
                loading={isFetching}
                isClearable
                data-qa-id="business-apps-search-input"
              />
            </PageToolbarSection>
            <PageToolbarTitle
              count={apps.length}
              single={t('App')}
              plural={t('Apps')}
            />
            <PageToolbarSection>
              <div className="flex items-center justify-end gap-[20px]">
                <LinkText
                  href={BUSINESS_APPS_DEVELOPER_DOCS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  qa={{ id: 'business-apps-developer-docs-link' }}
                >
                  {t('Developer docs')}
                </LinkText>
                {permissions.canEditWithKiCode ? (
                  <Button
                    color="primary"
                    outline
                    onClick={() => openKiCode(businessAppOpenArgs())}
                    qa={{ id: 'business-apps-create-with-kicode-button' }}
                  >
                    {kiCodeCopy(t).createWithKiCode}
                  </Button>
                ) : null}
                <Button
                  color="primary"
                  onClick={() => setShowCreateModal(true)}
                  qa={{ id: 'business-apps-create-button' }}
                >
                  {t('New Business App')}
                </Button>
              </div>
            </PageToolbarSection>
          </PageToolbar>
        }
        recordTabSpacing={false}
        additionalNavHeight={APPS_TABS_HEIGHT}
        {...bigTablePageProps}
      >
        <StyledTable>
          <BigTable
            tableTitle={
              hasNoApps ? (
                <StyledNotice>
                  {t(
                    'There are no Business Apps yet. Your coding agent builds and pushes them. To get started, click New Business App.'
                  )}
                </StyledNotice>
              ) : null
            }
            scrolledToTable={bigTablePageProps.scrolledToTable}
            columns={columns}
            head={<TRow head columns={columns} data={headData} />}
            staleData={isFetching}
          >
            {rows.length ? null : <tr />}
            {rows.map((row) => (
              <TRow
                key={row.id}
                columns={columns}
                data={row}
                data-row-kind={row.kind}
              />
            ))}
          </BigTable>
        </StyledTable>
      </BigTableLayout>
      {showCreateModal ? (
        <CreateBusinessAppModal onHide={() => setShowCreateModal(false)} />
      ) : null}
      {pending ? (
        <ConfirmActionModal
          pending={pending}
          onConfirm={confirmPending}
          onHide={cancelPending}
        />
      ) : null}
    </>
  );
};
